import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import TabletLayout from "./TabletLayout";
import mesApi from "../../../Api/mes.api";
import { TimelineBatch } from "../../../types/mes";
import { toast, ToastContainer } from "react-toastify";
import { formatClockTime } from "../../../utils/processParameterSerialize";

/**
 * Tablet Mixer View - Shows batches for a specific mixer
 * Auto-refreshes every 30 seconds
 * Large font, touch-friendly, read-only
 */
const TabletMixer = () => {
  const { mixer } = useParams<{ mixer?: string }>();
  const mixerName = mixer || "Mixer";

  const [batches, setBatches] = useState<TimelineBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    loadBatches();

    // Auto-refresh every 30 seconds
    const refreshInterval = setInterval(loadBatches, 30000);

    // Update clock every second
    const clockInterval = setInterval(() => setCurrentTime(new Date()), 1000);

    return () => {
      clearInterval(refreshInterval);
      clearInterval(clockInterval);
    };
  }, [mixerName]);

  const loadBatches = async () => {
    try {
      const today = new Date().toISOString().split("T")[0];
      // Production-day view: everything physically made today on this mixer,
      // incl. semi-finished (starter, enzimi…) made today for other days' plans,
      // sequenced together with the day's other products.
      const { batches: dayBatches } = await mesApi.getProductionDay(today);

      if (dayBatches.length) {
        const mixerBatches = dayBatches
          .filter((b: TimelineBatch) => b.MixerName === mixerName)
          .sort((a: TimelineBatch, b: TimelineBatch) => {
            const aTime = a.MixingStart ? new Date(a.MixingStart).getTime() : 0;
            const bTime = b.MixingStart ? new Date(b.MixingStart).getTime() : 0;
            return aTime - bTime;
          });

        setBatches(mixerBatches);
      } else {
        setBatches([]);
      }
    } catch (error: any) {
      toast.error(`Greška pri učitavanju: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateStr?: string) => formatClockTime(dateStr);

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; color: string }> = {
      planned: { label: "PLANIRANO", color: "#6c757d" },
      ready: { label: "SPREMNO", color: "#17a2b8" },
      in_progress: { label: "U TIJEKU", color: "#28a745" },
      completed: { label: "GOTOVO", color: "#007bff" },
      on_hold: { label: "NA ČEKANJU", color: "#ffc107" },
      canceled: { label: "OTKAZANO", color: "#dc3545" },
    };

    const statusInfo = statusMap[status] || { label: status.toUpperCase(), color: "#6c757d" };
    return (
      <span
        className="status-badge"
        style={{ background: statusInfo.color }}
      >
        {statusInfo.label}
      </span>
    );
  };

  const isCurrentBatch = (batch: TimelineBatch): boolean => {
    if (!batch.MixingStart || !batch.MixingEnd) return false;
    const now = currentTime.getTime();
    const start = new Date(batch.MixingStart).getTime();
    const end = new Date(batch.MixingEnd).getTime();
    return now >= start && now <= end;
  };

  return (
    <TabletLayout station={mixerName}>
      <ToastContainer position="top-center" autoClose={3000} />

      {loading ? (
        <div className="loading-state">
          <p>Učitavanje...</p>
        </div>
      ) : batches.length === 0 ? (
        <div className="empty-state">
          <p>Nema planiranih zamjesa za danas</p>
        </div>
      ) : (
        <div className="batches-list">
          {batches.map((batch) => {
            const isCurrent = isCurrentBatch(batch);
            return (
              <div
                key={batch.ID}
                className={`batch-card ${isCurrent ? "current-batch" : ""}`}
              >
                <div className="batch-header">
                  <div className="batch-time">{formatTime(batch.MixingStart)}</div>
                  {getStatusBadge(batch.BatchStatus)}
                </div>
                <div className="batch-id">{batch.MixCode}</div>
                <div className="batch-details">
                  <div className="detail-item">
                    <span className="detail-label">Proizvod:</span>
                    <span className="detail-value">{batch.ProductName || "-"}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Masa:</span>
                    <span className="detail-value">{batch.FinalMassKg?.toFixed(1)} kg</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Trajanje:</span>
                    <span className="detail-value">~{batch.MixingTimeMin} min</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Završetak:</span>
                    <span className="detail-value">{formatTime(batch.MixingEnd)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        .batches-list {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .batch-card {
          background: #2a2f3e;
          border-radius: 12px;
          padding: 2rem;
          border-left: 6px solid #6c757d;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
          transition: all 0.3s;
        }

        .batch-card.current-batch {
          border-left-color: #28a745;
          background: #2e3d2f;
          box-shadow: 0 6px 20px rgba(40, 167, 69, 0.3);
          animation: pulse 2s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% {
            box-shadow: 0 6px 20px rgba(40, 167, 69, 0.3);
          }
          50% {
            box-shadow: 0 8px 30px rgba(40, 167, 69, 0.5);
          }
        }

        .batch-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .batch-time {
          font-size: 3rem;
          font-weight: 700;
          color: #007bff;
          font-family: 'Courier New', monospace;
        }

        .status-badge {
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-size: 1.4rem;
          font-weight: 700;
          color: white;
          letter-spacing: 0.5px;
        }

        .batch-id {
          font-size: 2rem;
          font-weight: 600;
          color: #ffffff;
          margin-bottom: 1.5rem;
          word-break: break-word;
        }

        .batch-details {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
        }

        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .detail-label {
          font-size: 1.1rem;
          color: #adb3bd;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .detail-value {
          font-size: 1.8rem;
          font-weight: 600;
          color: #ffffff;
        }

        .loading-state,
        .empty-state {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 60vh;
          font-size: 2rem;
          color: #adb3bd;
        }

        @media (max-width: 768px) {
          .batch-time {
            font-size: 2.5rem;
          }

          .batch-id {
            font-size: 1.6rem;
          }

          .detail-value {
            font-size: 1.5rem;
          }

          .batch-details {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </TabletLayout>
  );
};

export default TabletMixer;
